import React, { useState, useEffect } from 'react';
import { Trophy, Award, Target, Zap, TrendingUp, Calendar, Star, Crown, Users, BarChart3, Filter, ChevronDown, ArrowUp, ArrowDown, Activity, Shuffle, X, Search, Eye, EyeOff, MoreVertical } from 'lucide-react';
import RecordBookBanner from './RecordBookBanner';
// Import the banner component (placeholder for demo)

const RecordBookComponent = ({ h2hData = [], finalData = [], loading = false, error = null }) => {
  const [recordsData, setRecordsData] = useState({});
  const [dataYearRange, setDataYearRange] = useState({ min: 2012, max: 2023 });
  const [filters, setFilters] = useState({
    gameTypes: ['Regular Season'],
    yearRange: [2012, 2023],
    topX: 5
  });
  const [expandedCategories, setExpandedCategories] = useState({
    singleSeason: true,
    singleGame: true,
    margins: true,
    teamRecords: true,
    rivalries: true
  });
  const [isMobile, setIsMobile] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleRecords, setVisibleRecords] = useState({});

  const gameTypeOptions = ['Regular Season', 'Playoffs', 'Consolation'];

  // Enhanced mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      
      // Auto-collapse certain categories on small screens
      if (width < 640) {
        setExpandedCategories(prev => ({
          ...prev,
          singleSeason: true,
          singleGame: false,
          margins: false,
          teamRecords: false,
          rivalries: false
        }));
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate dynamic year range from actual data
  useEffect(() => {
    if (h2hData.length > 0) {
      const years = h2hData.map(game => game.Year).filter(year => year && !isNaN(year));
      const minYear = Math.min(...years);
      const maxYear = Math.max(...years);
      setDataYearRange({ min: minYear, max: maxYear });
      setFilters(prev => ({
        ...prev,
        yearRange: [minYear, maxYear]
      }));
    }
  }, [h2hData]);

  useEffect(() => {
    if (h2hData.length > 0 && finalData.length > 0) {
      calculateRecords();
    }
  }, [h2hData, finalData, filters]);

  const calculateRecords = () => {
    try {
      // Filter data based on current filters
      const filteredH2H = h2hData.filter(game => 
        filters.gameTypes.includes(game.Type) &&
        game.Year >= filters.yearRange[0] && 
        game.Year <= filters.yearRange[1]
      );

      const filteredFinal = finalData.filter(season => 
        season.Year >= filters.yearRange[0] && 
        season.Year <= filters.yearRange[1]
      );

      // Calculate various records matching your R Shiny structure exactly
      const records = {
        singleSeason: calculateSingleSeasonRecords(filteredFinal, filteredH2H),
        singleGame: calculateSingleGameRecords(filteredH2H),
        margins: calculateMarginRecords(filteredH2H),
        teamRecords: calculateTeamRecords(filteredFinal),
        rivalries: calculateRivalryRecords(filteredH2H)
      };

      setRecordsData(records);
    } catch (err) {
      console.error('Error calculating records:', err);
    }
  };

  const getCurrentSeason = (data) => {
    return Math.max(...data.map(item => item.Year || 0));
  };

  const getCurrentWeek = (data) => {
    return Math.max(...data.map(item => item.LeagueWeek || 0));
  };

  // FIXED: Calculate weekly positions exactly like legacyscore.js
  const calculateWeeklyPositions = (regSeasonGames) => {
    const weeklyPositions = {
      first: 0, second: 0, third: 0, fourth: 0, fifth: 0, sixth: 0,
      seventh: 0, eighth: 0, ninth: 0, tenth: 0, eleventh: 0, twelfth: 0
    };
  
    regSeasonGames.forEach(game => {
      const winsThisWeek = game['Wins (This Week)'] || 0;
      
      // Map wins to weekly positions (11 wins = 1st place, 0 wins = 12th place)
      if (winsThisWeek === 11) weeklyPositions.first += 1;        // 1st place
      if (winsThisWeek === 10) weeklyPositions.second += 1;       // 2nd place
      if (winsThisWeek === 9) weeklyPositions.third += 1;        // 3rd place
      if (winsThisWeek === 8) weeklyPositions.fourth += 1;       // 4th place
      if (winsThisWeek === 7) weeklyPositions.fifth += 1;        // 5th place
      if (winsThisWeek === 6) weeklyPositions.sixth += 1;        // 6th place
      if (winsThisWeek === 5) weeklyPositions.seventh += 1;      // 7th place
      if (winsThisWeek === 4) weeklyPositions.eighth += 1;       // 8th place
      if (winsThisWeek === 3) weeklyPositions.ninth += 1;        // 9th place
      if (winsThisWeek === 2) weeklyPositions.tenth += 1;        // 10th place
      if (winsThisWeek === 1) weeklyPositions.eleventh += 1;     // 11th place
      if (winsThisWeek === 0) weeklyPositions.twelfth += 1;      // 12th place
    });
  
    return weeklyPositions;
  };

  const calculateSingleSeasonRecords = (finalData, h2hData) => {
    if (!h2hData.length) return {};

    // Calculate season aggregations exactly like R code
    const seasonStats = h2hData.reduce((acc, game) => {
      const key = `${game.Year}-${game.Manager1}`;
      if (!acc[key]) {
        acc[key] = { 
          year: game.Year, 
          manager: game.Manager1, 
          wins: 0, 
          losses: 0, 
          pf: 0, 
          pa: 0,
          gp: 0 
        };
      }
      acc[key].wins += game.IsWin || 0;
      acc[key].losses += game.IsLoss || 0;
      acc[key].pf += game.PF || 0;
      acc[key].pa += game.PA || 0;
      acc[key].gp += (game.IsWin || 0) + (game.IsLoss || 0);
      return acc;
    }, {});

    const seasonData = Object.values(seasonStats).map(season => ({
      ...season,
      winPct: season.wins / (season.wins + season.losses) || 0,
      pfPerGame: season.pf / season.gp || 0,
      paPerGame: season.pa / season.gp || 0
    }));

    const currentSeason = getCurrentSeason(h2hData);

    // Most single season wins (matching winsgt exactly)
    const mostWins = seasonData
      .sort((a, b) => b.wins - a.wins || b.winPct - a.winPct)
      .slice(0, filters.topX)
      .map(record => ({
        ...record,
        tenure: currentSeason - record.year
      }));

    // Most single season points (matching seasonptsgt - sorted by PF/G)
    const mostPoints = seasonData
      .sort((a, b) => b.pfPerGame - a.pfPerGame)
      .slice(0, filters.topX)
      .map(record => ({
        ...record,
        tenure: currentSeason - record.year
      }));

    // Most single season points against (matching seasonptsagainstgt)
    const mostPointsAgainst = seasonData
      .sort((a, b) => b.paPerGame - a.paPerGame)
      .slice(0, filters.topX)
      .map(record => ({
        ...record,
        tenure: currentSeason - record.year
      }));

    // Fewest single season points (matching seasonpfgtfewest)
    const fewestPoints = seasonData
      .sort((a, b) => a.pfPerGame - b.pfPerGame)
      .slice(0, filters.topX)
      .map(record => ({
        ...record,
        tenure: currentSeason - record.year
      }));

    // Fewest single season points against (matching seasonptsagainstgtfewest)
    const fewestPointsAgainst = seasonData
      .sort((a, b) => a.paPerGame - b.paPerGame)
      .slice(0, filters.topX)
      .map(record => ({
        ...record,
        tenure: currentSeason - record.year
      }));

    // Most transactions (matching mosttransactionsgt) - using finalData
    const transactionData = finalData
      .map(season => ({
        ...season,
        transPerWeek: season.Moves / (season.Wins + season.Losses) || 0
      }))
      .sort((a, b) => b.transPerWeek - a.transPerWeek || b.Moves - a.Moves)
      .slice(0, filters.topX)
      .map(record => ({
        ...record,
        tenure: currentSeason - record.Year
      }));

    return {
      mostWins,
      mostPoints,
      mostPointsAgainst,
      fewestPoints,
      fewestPointsAgainst,
      mostTransactions: transactionData
    };
  };

  const calculateSingleGameRecords = (h2hData) => {
    if (!h2hData.length) return {};

    const currentWeek = getCurrentWeek(h2hData);

    // Most points in a game (matching singlegamepts)
    const highestScoring = h2hData
      .filter(game => game.PF > 0)
      .sort((a, b) => b.PF - a.PF)
      .slice(0, filters.topX)
      .map(record => ({
        ...record,
        YearWeek: `${record.Year} Wk ${record.Week}`,
        tenure: currentWeek - (record.LeagueWeek || 0)
      }));

    // Fewest points in a game (matching singlegamefewestpts)
    const lowestScoring = h2hData
      .filter(game => game.PF > 0)
      .sort((a, b) => a.PF - b.PF)
      .slice(0, filters.topX)
      .map(record => ({
        ...record,
        YearWeek: `${record.Year} Wk ${record.Week}`,
        tenure: currentWeek - (record.LeagueWeek || 0)
      }));

    // Fewest points in a victory (matching fewestptsvic)
    const fewestPointsWin = h2hData
      .filter(game => game.Outcome === 'Win' && game.PF > 0)
      .sort((a, b) => a.PF - b.PF)
      .slice(0, filters.topX)
      .map(record => ({
        ...record,
        YearWeek: `${record.Year} Wk ${record.Week}`,
        tenure: currentWeek - (record.LeagueWeek || 0)
      }));

    // Most points in a loss (matching mostpointsl)
    const mostPointsLoss = h2hData
      .filter(game => game.Outcome === 'Loss' && game.PF > 0)
      .sort((a, b) => b.PF - a.PF)
      .slice(0, filters.topX)
      .map(record => ({
        ...record,
        YearWeek: `${record.Year} Wk ${record.Week}`,
        tenure: currentWeek - (record.LeagueWeek || 0)
      }));

    return {
      highestScoring,
      lowestScoring,
      fewestPointsWin,
      mostPointsLoss
    };
  };

  const calculateMarginRecords = (h2hData) => {
    if (!h2hData.length) return {};

    const currentWeek = getCurrentWeek(h2hData);

    // Largest margins of victory (matching largemov)
    const largestMargins = h2hData
      .filter(game => game.MoV > 0)
      .sort((a, b) => b.MoV - a.MoV)
      .slice(0, filters.topX)
      .map(record => ({
        ...record,
        YearWeek: `${record.Year} Wk ${record.Week}`,
        tenure: currentWeek - (record.LeagueWeek || 0)
      }));

    // Narrowest margins of victory (matching narrowmov)
    const narrowestMargins = h2hData
      .filter(game => game.Outcome === 'Win' && game.MoV >= 0)
      .sort((a, b) => a.MoV - b.MoV)
      .slice(0, filters.topX)
      .map(record => ({
        ...record,
        YearWeek: `${record.Year} Wk ${record.Week}`,
        tenure: currentWeek - (record.LeagueWeek || 0)
      }));

    return {
      largestMargins,
      narrowestMargins
    };
  };

  const calculateTeamRecords = (finalData) => {
    if (!finalData.length || !h2hData.length) return {};
  
    const seasonLegacyScores = [];
  
    // For each season/manager combination - EXACTLY like legacyscore.js
    finalData.forEach(season => {
      // Get all games for this manager in this year
      const managerH2H = h2hData.filter(game => 
        game.Manager1 === season.Owner && 
        game.Year === season.Year
      );
  
      const regSeasonGames = managerH2H.filter(game => game.Type === 'Regular Season');
      const playoffGames = managerH2H.filter(game => game.Type === 'Playoffs');
  
      // Calculate components exactly like legacyscore.js
      const regWins = regSeasonGames.reduce((sum, game) => sum + (game.IsWin || 0), 0);
      const regLosses = regSeasonGames.reduce((sum, game) => sum + (game.IsLoss || 0), 0);
      const weeklyPositions = calculateWeeklyPositions(regSeasonGames);
      const playoffWins = playoffGames.reduce((sum, game) => sum + (game.IsWin || 0), 0);
  
      // Calculate scoring champion/loser for this year - FIXED implementation
      const yearSeasons = finalData.filter(s => s.Year === season.Year);
      const maxPF = Math.max(...yearSeasons.map(s => s.PF || 0));
      const minPF = Math.min(...yearSeasons.map(s => s.PF || 0));
      const scoreChamp = (season.PF === maxPF) ? 1 : 0;
      const scoreLoser = (season.PF === minPF) ? 1 : 0;
  
      // Calculate Legacy Score using EXACT same formula as legacyscore.js
      const legacyScore = 
        // Regular season W/L
        regWins * 5 + regLosses * -5 +
        
        // Weekly scoring (CORRECTED multipliers to match legacyscore.js)
        weeklyPositions.first * 15 + weeklyPositions.twelfth * -15 +      // 1st/12th
        weeklyPositions.second * 5 + weeklyPositions.eleventh * -5 +      // 2nd/11th
        weeklyPositions.third * 4 + weeklyPositions.tenth * -4 +          // 3rd/10th
        weeklyPositions.fourth * 3 + weeklyPositions.ninth * -3 +         // 4th/9th
        weeklyPositions.fifth * 2 + weeklyPositions.eighth * -2 +         // 5th/8th
        weeklyPositions.sixth * 1 + weeklyPositions.seventh * -1 +        // 6th/7th
        
        // Playoff wins
        playoffWins * 10 +
        
        // Standings scoring (CORRECTED multipliers to match legacyscore.js)
        (season.Standings === 1 ? 125 : 0) +   // 1st place
        (season.Standings === 2 ? 75 : 0) +    // 2nd place
        (season.Standings === 3 ? 60 : 0) +    // 3rd place
        (season.Standings === 4 ? 40 : 0) +    // 4th place
        (season.Standings === 5 ? 30 : 0) +    // 5th place
        (season.Standings === 6 ? 15 : 0) +    // 6th place
        (season.Standings === 7 ? -15 : 0) +   // 7th place
        (season.Standings === 8 ? -30 : 0) +   // 8th place
        (season.Standings === 9 ? -40 : 0) +   // 9th place
        (season.Standings === 10 ? -50 : 0) +  // 10th place
        (season.Standings === 11 ? -60 : 0) +  // 11th place
        (season.Standings === 12 ? -80 : 0) +  // 12th place
        
        // Scoring champion/loser - FIXED calculation
        scoreChamp * 30 + scoreLoser * -30;
  
      seasonLegacyScores.push({
        manager: season.Owner,
        year: season.Year,
        team: season.Team,
        totalScore: Math.round(legacyScore),
        standings: season.Standings,
        wins: season.Wins,
        losses: season.Losses
      });
    });
  
    // Best single season teams (highest legacy score)
    const bestTeams = seasonLegacyScores
      .sort((a, b) => b.totalScore - a.totalScore)
      .slice(0, filters.topX);
  
    // Worst single season teams (lowest legacy score)
    const worstTeams = seasonLegacyScores
      .sort((a, b) => a.totalScore - b.totalScore)
      .slice(0, filters.topX);
  
    return {
      bestTeams,
      worstTeams
    };
  };
  
  const calculateRivalryRecords = (h2hData) => {
    if (!h2hData.length) return {};

    // Most lopsided rivalries (matching lopsidedrivalry exactly)
    const rivalries = h2hData
      .filter(game => game.Manager1 !== 'Sam' && game.Manager2 !== 'Sam') // Exclude Sam per your R code
      .reduce((acc, game) => {
        const key = `${game.Manager1}-${game.Manager2}`;
        if (!acc[key]) {
          acc[key] = {
            manager1: game.Manager1,
            manager2: game.Manager2,
            wins: 0,
            losses: 0
          };
        }
        acc[key].wins += game.IsWin || 0;
        acc[key].losses += game.IsLoss || 0;
        return acc;
      }, {});

    const rivalryData = Object.values(rivalries)
      .filter(rivalry => rivalry.wins + rivalry.losses > 0)
      .map(rivalry => ({
        ...rivalry,
        winPct: rivalry.wins / (rivalry.wins + rivalry.losses)
      }))
      .sort((a, b) => b.winPct - a.winPct)
      .slice(0, filters.topX);

    return {
      mostLopsided: rivalryData
    };
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const formatValue = (value, type) => {
    if (value === undefined || value === null) return 'N/A';
    
    switch (type) {
      case 'percentage':
        return `${(value * 100).toFixed(1)}%`;
      case 'decimal':
        return value.toFixed(1);
      case 'decimal2':
        return value.toFixed(2);
      case 'number':
        return Math.round(value).toString();
      case 'currency':
        return `$${value.toLocaleString()}`;
      default:
        return value.toString();
    }
  };

  // Helper function to get manager image - Updated to match LegacyScore.js
  const getManagerImage = (managerName) => {
    if (!managerName) return null;
    const mainImage = `/${managerName.toLowerCase()}main.png`;
    const fallbackImage = `/${managerName.toLowerCase()}.png`;
    
    return {
      main: mainImage,
      fallback: fallbackImage
    };
  };

  // Enhanced Mobile-First RecordTable Component
  const RecordTable = ({ title, data, columns, highlightFirst = true, color = 'green', footnoteText = null }) => {
    const [showAll, setShowAll] = useState(false);
    
    if (!data || data.length === 0) return null;

    // Mobile Card Layout - Completely redesigned
    const MobileCardLayout = () => {
      const displayData = isMobile && !showAll ? data.slice(0, 3) : data;
      
      return (
        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Mobile Header */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-3 py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h4 className="font-bold text-gray-800 text-sm leading-tight">{title}</h4>
              {isMobile && data.length > 3 && (
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="text-xs text-purple-600 font-medium flex items-center"
                >
                  {showAll ? (
                    <>
                      <EyeOff className="h-3 w-3 mr-1" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <Eye className="h-3 w-3 mr-1" />
                      Show All ({data.length})
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
          
          <div className="divide-y divide-gray-100">
            {displayData.map((record, idx) => (
              <div 
                key={idx}
                className={`p-3 ${
                  highlightFirst && idx === 0 
                    ? color === 'green' 
                      ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500' 
                      : 'bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-red-500'
                    : 'bg-white hover:bg-gray-50 transition-colors duration-150'
                }`}
              >
                {/* First place special treatment */}
                {highlightFirst && idx === 0 && (
                  <div className="flex items-center mb-3">
                    {/* Manager Headshot - Enhanced */}
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 border-2 border-white shadow-lg mr-3 flex-shrink-0 ring-2 ring-gray-100">
                      <img
                        src={getManagerImage(record.manager || record.Owner || record.Manager1 || record.manager1)?.main}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const images = getManagerImage(record.manager || record.Owner || record.Manager1 || record.manager1);
                          if (images?.fallback && e.target.src !== images.fallback) {
                            e.target.src = images.fallback;
                          } else {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                      <div className="w-full h-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center text-white text-sm font-bold" style={{display: 'none'}}>
                        {(record.manager || record.Owner || record.Manager1 || record.manager1)?.charAt(0)}
                      </div>
                    </div>
                    
                    <div className="flex flex-col">
                      <div className="flex items-center mb-1">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white mr-2 ${
                          color === 'green' ? 'bg-green-500 shadow-md' : 'bg-red-500 shadow-md'
                        }`}>
                          1
                        </div>
                        <span className={`text-xs font-semibold uppercase tracking-wide ${
                          color === 'green' ? 'text-green-700' : 'text-red-700'
                        }`}>
                          Record Holder
                        </span>
                      </div>
                      <div className={`text-sm font-bold ${
                        color === 'green' ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {record.manager || record.Owner || record.Manager1 || record.manager1}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Adaptive grid based on column count */}
                <div className={`grid gap-2 ${
                  columns.length <= 3 ? 'grid-cols-1' :
                  columns.length <= 6 ? 'grid-cols-2' :
                  'grid-cols-1'
                }`}>
                  {columns.slice(0, isMobile ? Math.min(4, columns.length) : columns.length).map((col, colIdx) => (
                    <div key={colIdx} className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500 truncate mr-2">
                        {col.label}
                      </span>
                      <span className={`text-xs font-semibold text-right ${
                        highlightFirst && idx === 0 
                          ? color === 'green' 
                            ? 'text-green-800' + (col.bold ? ' font-bold' : '')
                            : 'text-red-800' + (col.bold ? ' font-bold' : '')
                          : col.bold 
                            ? 'text-gray-900 font-bold' 
                            : 'text-gray-700'
                      }`}>
                        {col.format ? col.format(record[col.key]) : record[col.key]}
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Ranking indicator for non-first places */}
                {!(highlightFirst && idx === 0) && (
                  <div className="mt-2 flex justify-end">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      #{idx + 1}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {footnoteText && (
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 px-3 py-2 border-t border-gray-200">
              <p className="text-xs text-gray-600 leading-relaxed">
                {isMobile && footnoteText.length > 80 ? footnoteText.substring(0, 80) + '...' : footnoteText}
              </p>
            </div>
          )}
        </div>
      );
    };

    // Desktop Table Layout (Preserved from original)
    const DesktopTableLayout = () => (
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-bold text-gray-800">{title}</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white/90 backdrop-blur-sm divide-y divide-gray-200">
              {data.map((record, idx) => (
                <tr 
                  key={idx}
                  className={
                    highlightFirst && idx === 0 
                      ? color === 'green' 
                        ? 'bg-green-600 text-white' 
                        : 'bg-red-600 text-white'
                      : 'hover:bg-gray-50'
                  }
                >
                  {columns.map((col, colIdx) => (
                    <td 
                      key={colIdx} 
                      className={`px-6 py-4 whitespace-nowrap text-sm ${
                        highlightFirst && idx === 0 
                          ? 'text-white ' + (col.bold ? 'font-bold text-lg' : '')
                          : col.bold 
                            ? 'font-bold text-gray-900' 
                            : 'text-gray-900'
                      }`}
                    >
                      {/* Enhanced first place row with headshot */}
                      {highlightFirst && idx === 0 && (col.key === 'manager' || col.key === 'Owner' || col.key === 'Manager1' || col.key === 'manager1') ? (
                        <div className="flex items-center">
                          <div className="w-12 h-12 rounded-full overflow-hidden bg-white/20 border-2 border-white/40 mr-4 flex-shrink-0 shadow-lg ring-2 ring-white/20">
                            <img
                              src={getManagerImage(record[col.key])?.main}
                              alt={`${record[col.key]} headshot`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const images = getManagerImage(record[col.key]);
                                if (images?.fallback && e.target.src !== images.fallback) {
                                  e.target.src = images.fallback;
                                } else {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }
                              }}
                            />
                            <div className="w-full h-full bg-white/20 flex items-center justify-center text-white text-lg font-bold" style={{display: 'none'}}>
                              {record[col.key]?.charAt(0)}
                            </div>
                          </div>
                          <span className="font-bold text-lg">
                            {col.format ? col.format(record[col.key]) : record[col.key]}
                          </span>
                        </div>
                      ) : (
                        col.format ? col.format(record[col.key]) : record[col.key]
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {footnoteText && (
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 px-6 py-3 border-t border-gray-200">
            <p className="text-xs text-gray-600">{footnoteText}</p>
          </div>
        )}
      </div>
    );

    return isMobile ? <MobileCardLayout /> : <DesktopTableLayout />;
  };

  // Mobile-Optimized Category Section
  const CategorySection = ({ title, icon: Icon, category, children }) => (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
      <button
        onClick={() => toggleCategory(category)}
        className={`w-full px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-5 bg-gradient-to-r from-purple-50 to-blue-50 hover:from-purple-100 hover:to-blue-100 transition-colors duration-200 flex items-center justify-between ${
          isMobile ? 'active:bg-purple-200' : ''
        }`}
      >
        <div className="flex items-center">
          <Icon className={`text-purple-600 mr-2 sm:mr-3 ${
            isMobile ? 'h-4 w-4' : 'h-5 w-5 lg:h-6 lg:w-6'
          }`} />
          <h3 className={`font-bold text-gray-800 ${
            isMobile ? 'text-sm' : 'text-base lg:text-xl'
          }`}>
            {title}
          </h3>
        </div>
        <ChevronDown 
          className={`text-gray-500 transition-transform duration-200 ${
            isMobile ? 'h-4 w-4' : 'h-5 w-5'
          } ${expandedCategories[category] ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {expandedCategories[category] && (
        <div className={`px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6 space-y-3 sm:space-y-4 lg:space-y-6 ${
          isMobile ? 'bg-gray-50' : ''
        }`}>
          {children}
        </div>
      )}
    </div>
  );

  // Mobile-Optimized Filters
  const MobileFilters = () => (
    <div className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${
      showFilters ? 'opacity-100' : 'opacity-0 pointer-events-none'
    }`}>
      <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl transform transition-transform duration-300 ${
        showFilters ? 'translate-y-0' : 'translate-y-full'
      }`}>
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800">Filters</h3>
          <button
            onClick={() => setShowFilters(false)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-4 max-h-96 overflow-y-auto space-y-6">
          {/* Year Range */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2">Season Range</label>
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-200 mb-3">
              <div className="text-center font-bold text-purple-800">
                {filters.yearRange[0]} - {filters.yearRange[1]}
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-xs text-gray-500 font-medium">
                <span>{dataYearRange.min}</span>
                <span>{dataYearRange.max}</span>
              </div>
              
              {/* Dual-handle range slider like HeadToHead */}
              <div className="range-slider-container">
                <div 
                  className="range-slider-track"
                  style={{
                    left: `${((filters.yearRange[0] - dataYearRange.min) / (dataYearRange.max - dataYearRange.min)) * 100}%`,
                    width: `${((filters.yearRange[1] - filters.yearRange[0]) / (dataYearRange.max - dataYearRange.min)) * 100}%`
                  }}
                />
                <input
                  type="range"
                  min={dataYearRange.min}
                  max={dataYearRange.max}
                  value={filters.yearRange[0]}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value <= filters.yearRange[1]) {
                      setFilters(prev => ({ ...prev, yearRange: [value, prev.yearRange[1]] }));
                    }
                  }}
                  className="range-slider"
                  style={{ zIndex: 2 }}
                />
                <input
                  type="range"
                  min={dataYearRange.min}
                  max={dataYearRange.max}
                  value={filters.yearRange[1]}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= filters.yearRange[0]) {
                      setFilters(prev => ({ ...prev, yearRange: [prev.yearRange[0], value] }));
                    }
                  }}
                  className="range-slider"
                  style={{ zIndex: 1 }}
                />
              </div>
              
              <div className="flex justify-between text-xs font-medium text-gray-700">
                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">
                  {filters.yearRange[0]}
                </span>
                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">
                  {filters.yearRange[1]}
                </span>
              </div>
            </div>
          </div>

          {/* Game Types */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2">Game Types</label>
            <div className="space-y-2">
              {gameTypeOptions.map(type => (
                <label key={type} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.gameTypes.includes(type)}
                    onChange={(e) => {
                      const newTypes = e.target.checked
                        ? [...filters.gameTypes, type]
                        : filters.gameTypes.filter(t => t !== type);
                      setFilters(prev => ({ ...prev, gameTypes: newTypes }));
                    }}
                    className="mr-3 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">{type}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Top X Records */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2">Records Shown</label>
            <div className="bg-green-50 rounded-lg p-3 border border-green-200 mb-3">
              <div className="text-center font-bold text-green-800">
                Top {filters.topX}
              </div>
            </div>
            <input
              type="range"
              min="1"
              max="30"
              value={filters.topX}
              onChange={(e) => setFilters(prev => ({ ...prev, topX: parseInt(e.target.value) }))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 font-medium mt-1">
              <span>1</span>
              <span>30</span>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => setShowFilters(false)}
            className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );

  const DesktopFilters = () => (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-3 sm:p-6 lg:p-8">
      <div className="flex items-center mb-3 sm:mb-6">
        <Filter className={`mr-2 sm:mr-3 text-purple-600 ${
          isMobile ? 'h-4 w-4' : 'h-5 w-5 sm:h-6 sm:w-6'
        }`} />
        <h3 className={`font-bold text-gray-800 ${
          isMobile ? 'text-base' : 'text-lg sm:text-xl lg:text-2xl'
        }`}>
          Filters
        </h3>
      </div>
      
      <div className={`grid gap-3 sm:gap-6 ${
        isMobile ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
      }`}>
        {/* Year Range - Mobile Optimized */}
        <div className="space-y-2">
          <div className="border-b border-gray-200 pb-2">
            <label className={`block font-bold text-gray-800 mb-1 ${
              isMobile ? 'text-sm' : 'text-sm'
            }`}>
              Seasons
            </label>
            <p className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-xs'}`}>
              {isMobile ? 'Season range' : 'Adjust the season range for records'}
            </p>
          </div>
          <div className="space-y-3 sm:space-y-4">
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-2 sm:p-3 border border-purple-200">
              <div className={`font-bold text-purple-800 text-center ${
                isMobile ? 'text-base' : 'text-base sm:text-lg lg:text-xl'
              }`}>
                {filters.yearRange[0]} - {filters.yearRange[1]}
              </div>
              <div className={`text-purple-600 text-center mt-1 ${
                isMobile ? 'text-xs' : 'text-xs sm:text-sm'
              }`}>
                Season range for records
              </div>
            </div>
            
            {/* Single range slider */}
            <div className="space-y-3">
              <div className="flex justify-between text-xs text-gray-500 px-1 font-medium">
                <span>{dataYearRange.min}</span>
                <span>{dataYearRange.max}</span>
              </div>
              
              <input
                type="range"
                min={dataYearRange.min}
                max={dataYearRange.max}
                value={filters.yearRange[0]}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value <= filters.yearRange[1]) {
                    setFilters(prev => ({ ...prev, yearRange: [value, prev.yearRange[1]] }));
                  }
                }}
                className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer"
              />
              
              <div className="flex justify-between text-xs font-medium text-gray-700 px-1">
                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                  {filters.yearRange[0]}
                </span>
                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                  {filters.yearRange[1]}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Game Types - Mobile Optimized */}
        <div className="space-y-2">
          <div className="border-b border-gray-200 pb-2">
            <label className={`block font-bold text-gray-800 mb-1 ${
              isMobile ? 'text-sm' : 'text-sm'
            }`}>
              Game Types
            </label>
            <p className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-xs'}`}>
              {isMobile ? 'Game types' : 'Select which game types to include in records'}
            </p>
          </div>
          <div className="space-y-2 sm:space-y-3">
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-2 sm:p-3 border border-blue-200">
              <div className={`font-bold text-blue-800 text-center ${
                isMobile ? 'text-base' : 'text-base sm:text-lg lg:text-xl'
              }`}>
                {filters.gameTypes.length} Type{filters.gameTypes.length !== 1 ? 's' : ''}
              </div>
              <div className={`text-blue-600 text-center mt-1 ${
                isMobile ? 'text-xs' : 'text-xs sm:text-sm'
              }`}>
                {isMobile ? filters.gameTypes.join(', ').substring(0, 20) + (filters.gameTypes.join(', ').length > 20 ? '...' : '') : filters.gameTypes.join(', ')}
              </div>
            </div>
            
            <div className="space-y-1 sm:space-y-2">
              {gameTypeOptions.map(type => (
                <label key={type} className="flex items-center cursor-pointer text-xs sm:text-sm">
                  <input
                    type="checkbox"
                    checked={filters.gameTypes.includes(type)}
                    onChange={(e) => {
                      const newTypes = e.target.checked
                        ? [...filters.gameTypes, type]
                        : filters.gameTypes.filter(t => t !== type);
                      setFilters(prev => ({ ...prev, gameTypes: newTypes }));
                    }}
                    className="mr-2 h-3 w-3 sm:h-4 sm:w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-gray-700">{type}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Top X Records - Mobile Optimized */}
        <div className="space-y-2">
          <div className="border-b border-gray-200 pb-2">
            <label className={`block font-bold text-gray-800 mb-1 ${
              isMobile ? 'text-sm' : 'text-sm'
            }`}>
              Records Shown
            </label>
            <p className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-xs'}`}>
              {isMobile ? 'Per category' : 'Number of records to display per category'}
            </p>
          </div>
          <div className="space-y-3 sm:space-y-4">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-2 sm:p-3 border border-green-200">
              <div className={`font-bold text-green-800 text-center ${
                isMobile ? 'text-base' : 'text-base sm:text-lg lg:text-xl'
              }`}>
                Top {filters.topX}
              </div>
              <div className={`text-green-600 text-center mt-1 ${
                isMobile ? 'text-xs' : 'text-xs sm:text-sm'
              }`}>
                Records per category
              </div>
            </div>
            <input
              type="range"
              min="1"
              max="30"
              value={filters.topX}
              onChange={(e) => setFilters(prev => ({ ...prev, topX: parseInt(e.target.value) }))}
              className="w-full h-2 sm:h-3 bg-gradient-to-r from-green-200 to-emerald-200 rounded-lg appearance-none cursor-pointer slider-thumb-green"
            />
            <div className="flex justify-between text-xs text-gray-500 font-medium">
              <span>1</span>
              <span>30</span>
            </div>
          </div>
        </div>
      </div>

      {/* Records Display Info - Mobile Optimized */}
      <div className="mt-4 sm:mt-6 bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg p-3 sm:p-4 border border-gray-200">
        <div className="text-center">
          <div className={`text-gray-700 font-medium ${isMobile ? 'text-sm' : 'text-sm'}`}>
            Across {filters.gameTypes.length} game type{filters.gameTypes.length !== 1 ? 's' : ''}
          </div>
          <div className={`text-gray-500 text-center mt-1 ${isMobile ? 'text-xs' : 'text-xs'}`}>
            {isMobile ? filters.gameTypes.join(', ').substring(0, 30) + (filters.gameTypes.join(', ').length > 30 ? '...' : '') : filters.gameTypes.join(', ')}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <RecordBookBanner />
        <div className="flex items-center justify-center h-48 sm:h-64">
          <div className={`animate-spin rounded-full border-b-2 border-purple-600 ${
            isMobile ? 'h-8 w-8' : 'h-12 w-12'
          }`}></div>
          <span className={`ml-3 text-gray-600 ${isMobile ? 'text-base' : 'text-lg'}`}>
            Loading record book...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <RecordBookBanner />
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6">
          <h3 className="text-red-800 font-semibold text-sm sm:text-base">Error loading record book</h3>
          <p className="text-red-600 mt-2 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8">
      <RecordBookBanner />

      {/* Mobile Filter Button */}
      {isMobile && (
        <div className="flex items-center justify-between bg-white/90 rounded-xl shadow-lg border border-gray-200 p-3">
          <div className="flex items-center">
            <Filter className="h-4 w-4 mr-2 text-purple-600" />
            <span className="text-sm font-semibold text-gray-800">
              {filters.gameTypes.length} type{filters.gameTypes.length !== 1 ? 's' : ''}, {filters.yearRange[0]}-{filters.yearRange[1]}
            </span>
          </div>
          <button
            onClick={() => setShowFilters(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors"
          >
            Filters
          </button>
        </div>
      )}

      {/* Desktop Filters */}
      {!isMobile && <DesktopFilters />}

      {/* Mobile Filters Modal */}
      {isMobile && <MobileFilters />}

      {/* Records Sections - Mobile Optimized */}
      <div className="space-y-4 sm:space-y-6">
        {/* Single Season Records */}
        <CategorySection title="Single Season Records" icon={Calendar} category="singleSeason">
          <div className={`grid gap-3 sm:gap-6 ${
            isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'
          }`}>
            {recordsData.singleSeason?.mostWins && (
              <RecordTable
                title="Most Single Season Wins"
                data={recordsData.singleSeason.mostWins}
                columns={[
                  { key: 'manager', label: 'Manager' },
                  { key: 'year', label: 'Season' },
                  { key: 'wins', label: 'Wins', bold: true },
                  { key: 'losses', label: 'Losses' },
                  { key: 'winPct', label: 'Win %', format: (val) => formatValue(val, 'percentage') }
                ]}
                color="green"
                footnoteText={recordsData.singleSeason.mostWins[0] ? 
                  `The single season wins record has stood for ${recordsData.singleSeason.mostWins[0].tenure} season(s).` : null}
              />
            )}

            {recordsData.singleSeason?.mostPoints && (
              <RecordTable
                title="Most Single Season Points"
                data={recordsData.singleSeason.mostPoints}
                columns={[
                  { key: 'manager', label: 'Manager' },
                  { key: 'year', label: 'Season' },
                  { key: 'gp', label: 'GP' },
                  { key: 'pf', label: 'Points', bold: true, format: (val) => formatValue(val, 'decimal') },
                  { key: 'pfPerGame', label: 'Points/Game', format: (val) => formatValue(val, 'decimal2') }
                ]}
                color="green"
                footnoteText={recordsData.singleSeason.mostPoints[0] ? 
                  `The single season points record has stood for ${recordsData.singleSeason.mostPoints[0].tenure} season(s).` : null}
              />
            )}

            {recordsData.singleSeason?.mostPointsAgainst && (
              <RecordTable
                title="Most Single Season Points Against"
                data={recordsData.singleSeason.mostPointsAgainst}
                columns={[
                  { key: 'manager', label: 'Manager' },
                  { key: 'year', label: 'Season' },
                  { key: 'gp', label: 'GP' },
                  { key: 'pa', label: 'Points Against', bold: true, format: (val) => formatValue(val, 'decimal') },
                  { key: 'paPerGame', label: 'PA/Game', format: (val) => formatValue(val, 'decimal2') }
                ]}
                color="red"
                footnoteText={recordsData.singleSeason.mostPointsAgainst[0] ? 
                  `The single season most points against record has stood for ${recordsData.singleSeason.mostPointsAgainst[0].tenure} season(s).` : null}
              />
            )}

            {recordsData.singleSeason?.fewestPoints && (
              <RecordTable
                title="Fewest Single Season Points"
                data={recordsData.singleSeason.fewestPoints}
                columns={[
                  { key: 'manager', label: 'Manager' },
                  { key: 'year', label: 'Season' },
                  { key: 'gp', label: 'GP' },
                  { key: 'pf', label: 'Points', bold: true, format: (val) => formatValue(val, 'decimal') },
                  { key: 'pfPerGame', label: 'Points/Game', format: (val) => formatValue(val, 'decimal2') }
                ]}
                color="red"
                footnoteText={recordsData.singleSeason.fewestPoints[0] ? 
                  `The single season fewest points record has stood for ${recordsData.singleSeason.fewestPoints[0].tenure} season(s).` : null}
              />
            )}

            {recordsData.singleSeason?.fewestPointsAgainst && (
              <RecordTable
                title="Fewest Single Season Points Against"
                data={recordsData.singleSeason.fewestPointsAgainst}
                columns={[
                  { key: 'manager', label: 'Manager' },
                  { key: 'year', label: 'Season' },
                  { key: 'gp', label: 'GP' },
                  { key: 'pa', label: 'Points Against', bold: true, format: (val) => formatValue(val, 'decimal') },
                  { key: 'paPerGame', label: 'PA/Game', format: (val) => formatValue(val, 'decimal2') }
                ]}
                color="green"
                footnoteText={recordsData.singleSeason.fewestPointsAgainst[0] ? 
                  `The single season fewest points against record has stood for ${recordsData.singleSeason.fewestPointsAgainst[0].tenure} season(s).` : null}
              />
            )}

            {recordsData.singleSeason?.mostTransactions && (
              <RecordTable
                title="Most Single Season Transactions"
                data={recordsData.singleSeason.mostTransactions}
                columns={[
                  { key: 'Owner', label: 'Manager' },
                  { key: 'Year', label: 'Season' },
                  { key: 'Moves', label: 'Moves', bold: true },
                  { key: 'transPerWeek', label: 'Moves/Week', format: (val) => formatValue(val, 'decimal2') }
                ]}
                color="green"
                footnoteText={recordsData.singleSeason.mostTransactions[0] ? 
                  `The single season most transactions record has stood for ${recordsData.singleSeason.mostTransactions[0].tenure} season(s).` : null}
              />
            )}
          </div>
        </CategorySection>

        {/* Single Game Records */}
        <CategorySection title="Single Game Records" icon={Zap} category="singleGame">
          <div className={`grid gap-3 sm:gap-6 ${
            isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'
          }`}>
            {recordsData.singleGame?.highestScoring && (
              <RecordTable
                title="Most Points Scored in a Game"
                data={recordsData.singleGame.highestScoring}
                columns={[
                  { key: 'YearWeek', label: 'Week' },
                  { key: 'Manager1', label: 'Manager' },
                  { key: 'PF', label: 'Points', bold: true, format: (val) => formatValue(val, 'decimal') },
                  { key: 'Manager2', label: 'Opponent' },
                  { key: 'PA', label: 'Points Against', format: (val) => formatValue(val, 'decimal') }
                ]}
                color="green"
                footnoteText={recordsData.singleGame.highestScoring[0] ? 
                  `The single game most points scored by a single manager record has stood for ${recordsData.singleGame.highestScoring[0].tenure} weeks of play.` : null}
              />
            )}

            {recordsData.singleGame?.lowestScoring && (
              <RecordTable
                title="Fewest Points Scored in a Game"
                data={recordsData.singleGame.lowestScoring}
                columns={[
                  { key: 'YearWeek', label: 'Week' },
                  { key: 'Manager1', label: 'Manager' },
                  { key: 'PF', label: 'Points', bold: true, format: (val) => formatValue(val, 'decimal') },
                  { key: 'Manager2', label: 'Opponent' },
                  { key: 'PA', label: 'Points Against', format: (val) => formatValue(val, 'decimal') }
                ]}
                color="red"
                footnoteText={recordsData.singleGame.lowestScoring[0] ? 
                  `The single game fewest points scored by a single manager record has stood for ${recordsData.singleGame.lowestScoring[0].tenure} weeks of play.` : null}
              />
            )}

            {recordsData.singleGame?.fewestPointsWin && (
              <RecordTable
                title="Fewest Points Scored in a Victory"
                data={recordsData.singleGame.fewestPointsWin}
                columns={[
                  { key: 'YearWeek', label: 'Week' },
                  { key: 'Manager1', label: 'Winner' },
                  { key: 'PF', label: 'Points', bold: true, format: (val) => formatValue(val, 'decimal') },
                  { key: 'Manager2', label: 'Loser' },
                  { key: 'PA', label: 'Points Against', format: (val) => formatValue(val, 'decimal') }
                ]}
                color="green"
                footnoteText={recordsData.singleGame.fewestPointsWin[0] ? 
                  `The single game fewest points scored in a victory record has stood for ${recordsData.singleGame.fewestPointsWin[0].tenure} weeks of play.` : null}
              />
            )}

            {recordsData.singleGame?.mostPointsLoss && (
              <RecordTable
                title="Most Points Scored in a Loss"
                data={recordsData.singleGame.mostPointsLoss}
                columns={[
                  { key: 'YearWeek', label: 'Week' },
                  { key: 'Manager1', label: 'Loser' },
                  { key: 'PF', label: 'Points', bold: true, format: (val) => formatValue(val, 'decimal') },
                  { key: 'Manager2', label: 'Winner' },
                  { key: 'PA', label: 'Winner Score', format: (val) => formatValue(val, 'decimal') }
                ]}
                color="red"
                footnoteText={recordsData.singleGame.mostPointsLoss[0] ? 
                  `The single game most points scored in a loss record has stood for ${recordsData.singleGame.mostPointsLoss[0].tenure} weeks of play.` : null}
              />
            )}
          </div>
        </CategorySection>

        {/* Margin Records */}
        <CategorySection title="Margin of Victory Records" icon={Target} category="margins">
          <div className={`grid gap-3 sm:gap-6 ${
            isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'
          }`}>
            {recordsData.margins?.largestMargins && (
              <RecordTable
                title="Largest Margin of Victory"
                data={recordsData.margins.largestMargins}
                columns={[
                  { key: 'YearWeek', label: 'Week' },
                  { key: 'Manager1', label: 'Winner' },
                  { key: 'PF', label: 'Winner Score', format: (val) => formatValue(val, 'decimal') },
                  { key: 'Manager2', label: 'Loser' },
                  { key: 'PA', label: 'Loser Score', format: (val) => formatValue(val, 'decimal') },
                  { key: 'MoV', label: 'Margin of Victory', bold: true, format: (val) => formatValue(val, 'decimal') }
                ]}
                color="green"
                footnoteText={recordsData.margins.largestMargins[0] ? 
                  `The largest margin of victory record has stood for ${recordsData.margins.largestMargins[0].tenure} weeks of play.` : null}
              />
            )}

            {recordsData.margins?.narrowestMargins && (
              <RecordTable
                title="Narrowest Margin of Victory"
                data={recordsData.margins.narrowestMargins}
                columns={[
                  { key: 'YearWeek', label: 'Week' },
                  { key: 'Manager1', label: 'Winner' },
                  { key: 'PF', label: 'Winner Score', format: (val) => formatValue(val, 'decimal') },
                  { key: 'Manager2', label: 'Loser' },
                  { key: 'PA', label: 'Loser Score', format: (val) => formatValue(val, 'decimal') },
                  { key: 'MoV', label: 'Margin of Victory', bold: true, format: (val) => formatValue(val, 'decimal') }
                ]}
                color="red"
                footnoteText={recordsData.margins.narrowestMargins[0] ? 
                  `The narrowest margin of victory record has stood for ${recordsData.margins.narrowestMargins[0].tenure} weeks of play.` : null}
              />
            )}
          </div>
        </CategorySection>

        {/* Team Records */}
        <CategorySection title="Team Records (Legacy Score)" icon={Star} category="teamRecords">
          <div className={`grid gap-3 sm:gap-6 ${
            isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'
          }`}>
            {recordsData.teamRecords?.bestTeams && (
              <RecordTable
                title="Best Team of All Time"
                data={recordsData.teamRecords.bestTeams}
                columns={[
                  { key: 'manager', label: 'Manager' },
                  { key: 'year', label: 'Season' },
                  { key: 'team', label: 'Team Name' },
                  { key: 'totalScore', label: 'Legacy Score', bold: true },
                  { key: 'standings', label: 'Finish' }
                ]}
                color="green"
                footnoteText="Best teams based on comprehensive Legacy Score calculation including wins, standings, and weekly performance."
              />
            )}

            {recordsData.teamRecords?.worstTeams && (
              <RecordTable
                title="Worst Team of All Time"
                data={recordsData.teamRecords.worstTeams}
                columns={[
                  { key: 'manager', label: 'Manager' },
                  { key: 'year', label: 'Season' },
                  { key: 'team', label: 'Team Name' },
                  { key: 'totalScore', label: 'Legacy Score', bold: true },
                  { key: 'standings', label: 'Finish' }
                ]}
                color="red"
                footnoteText="Worst teams based on comprehensive Legacy Score calculation including wins, standings, and weekly performance."
              />
            )}
          </div>
        </CategorySection>

        {/* Rivalries */}
        <CategorySection title="Rivalry Records" icon={Users} category="rivalries">
          <div className={`grid gap-3 sm:gap-6 ${
            isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'
          }`}>
            {recordsData.rivalries?.mostLopsided && (
              <RecordTable
                title="Most Lopsided Rivalries"
                data={recordsData.rivalries.mostLopsided}
                columns={[
                  { key: 'manager1', label: 'Manager' },
                  { key: 'manager2', label: 'Opponent' },
                  { key: 'wins', label: 'Wins', bold: true },
                  { key: 'losses', label: 'Losses' },
                  { key: 'winPct', label: 'Win %', format: (val) => formatValue(val, 'percentage') }
                ]}
                color="green"
                footnoteText="Most dominant head-to-head records between managers (excluding Sam)."
              />
            )}
          </div>
        </CategorySection>
      </div>
    </div>
  );
};

export default RecordBookComponent;